/**
 * OAuth Settings Page
 * Manage OAuth provider connections - separated into Auth and Repository sections.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import axios from 'axios';

import Footer from '../../components/Footer';
import { Navbar } from '../../components/Navbar';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ReauthBanner } from '../../components/ReauthBanner';
import api from '../../services/authenticatedApi';
import { analytics } from '../../services/analytics/posthog';
import { logger } from '../../utils/logger';

import type {
  DisconnectModalState,
  OAuthConnection,
  PasswordFormData,
  ProfileFormData,
  Provider,
  PurposeType,
  ReauthConnection,
  TabKey,
} from './types';
import { getProviderIcon } from './getProviderIcon';
import { isAuthProvider, isRepositoryProvider } from './utils';
import { ProfileTab } from './ProfileTab';
import { AuthProvidersTab } from './AuthProvidersTab';
import { RepoProvidersTab } from './RepoProvidersTab';
import { ApiTokensTab } from './ApiTokensTab';
import { TabNav } from './TabNav';

const VALID_TABS: TabKey[] = ['profile', 'auth', 'repository', 'api-tokens'];

export const OAuthSettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get active tab from URL, default to 'profile'
  const tabParam = searchParams.get('tab') as TabKey | null;
  const [activeTab, setActiveTab] = useState<TabKey>(
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'profile',
  );

  // Profile state
  const [profileData, setProfileData] = useState<ProfileFormData>({
    firstName: '',
    email: '',
    company: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [hasPassword, setHasPassword] = useState<boolean>(true); // Default to true to avoid showing wrong form initially

  // Password change state
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Track failed avatar images
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set());

  // Reauth status state
  const [reauthConnections, setReauthConnections] = useState<ReauthConnection[]>([]);

  // Disconnect confirmation modal state
  const [disconnectModal, setDisconnectModal] = useState<DisconnectModalState>({
    isOpen: false,
    connectionId: null,
    providerName: '',
    username: '',
    purpose: '',
  });

  // Sync activeTab with URL param on mount and when URL changes
  useEffect(() => {
    const nextTab = searchParams.get('tab') as TabKey | null;
    if (nextTab && VALID_TABS.includes(nextTab)) {
      setActiveTab(nextTab);
    } else if (!nextTab) {
      // If no tab param, set default to profile
      setActiveTab('profile');
    }
  }, [searchParams]);

  useEffect(() => {
    analytics.pageViewed('settings_oauth');
  }, []);

  useEffect(() => {
    loadData();
    loadProfileData();
    loadReauthStatus();
  }, []);

  // Function to change tab and update URL
  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const loadProfileData = async () => {
    try {
      const response = await api.get('/auth/me');
      const { user } = response.data;
      setProfileData({
        firstName: user.firstName || '',
        email: user.email || '',
        company: user.company || '',
      });
      setHasPassword(user.hasPassword !== undefined ? user.hasPassword : true); // Load password status
    } catch (err: unknown) {
      logger.error('Failed to load profile', { error: err });
    }
  };

  const loadReauthStatus = async () => {
    try {
      const response = await api.get('/oauth/reauth-status');
      const { needsReauth, connections } = response.data;
      if (needsReauth) {
        setReauthConnections(connections);
      }
    } catch (err: unknown) {
      logger.error('Failed to load reauth status', { error: err });
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileSuccess(null);
    setError(null);

    try {
      const { email: _omit, ...updateData } = profileData; // eslint-disable-line @typescript-eslint/no-unused-vars
      await api.put('/auth/profile', updateData);
      setProfileSuccess('Profile updated successfully');
      setTimeout(() => setProfileSuccess(null), 3000);
    } catch (err: unknown) {
      logger.error('Failed to update profile', { error: err });
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Failed to update profile'
          : 'Failed to update profile',
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordSuccess(null);
    setPasswordError(null);

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    // Validate password strength
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      setPasswordLoading(false);
      return;
    }

    try {
      if (hasPassword) {
        // User has password - change it
        await api.post('/auth/change-password', {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        });
        setPasswordSuccess('Password changed successfully');
      } else {
        // User doesn't have password - set it for the first time
        await api.post('/auth/set-password', {
          newPassword: passwordData.newPassword,
        });
        setPasswordSuccess('Password set successfully');
        setHasPassword(true); // Update state to reflect password is now set
      }

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordSuccess(null), 3000);
    } catch (err: unknown) {
      logger.error('Failed to change/set password', { error: err });
      setPasswordError(
        axios.isAxiosError(err)
          ? err.response?.data?.error || 'Failed to update password'
          : 'Failed to update password',
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get available providers
      const providersRes = await api.get('/oauth/providers');
      const { providers: availableProviders } = providersRes.data;

      // Get user's connections
      const connectionsRes = await api.get('/oauth/connections');
      const { connections } = connectionsRes.data;

      // Map providers with their connections separated by purpose
      const providerList: Provider[] = availableProviders.map((name: string) => {
        const providerConnections = connections.filter((c: OAuthConnection) => c.provider === name);

        return {
          name,
          displayName: name.charAt(0).toUpperCase() + name.slice(1),
          icon: getProviderIcon(name),
          authConnections: providerConnections.filter(
            (c: OAuthConnection) => c.purpose === 'auth' || c.purpose === 'both',
          ),
          repoConnections: providerConnections.filter(
            (c: OAuthConnection) => c.purpose === 'repository' || c.purpose === 'both',
          ),
        };
      });

      setProviders(providerList);
    } catch (err: unknown) {
      logger.error('Failed to load OAuth data', { error: err });
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || err.message || 'Failed to load OAuth data');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load OAuth data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (providerName: string, purpose: PurposeType) => {
    try {
      const response = await api.post(`/oauth/${providerName}/initiate?purpose=${purpose}`);
      const { authorizationUrl } = response.data;

      // Redirect to provider's OAuth page
      window.location.href = authorizationUrl;
    } catch (err: unknown) {
      logger.error('Failed to connect provider', { error: err });
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || err.message || 'Failed to connect provider');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to connect provider');
      }
    }
  };

  const handleReauthorize = (provider: string) => {
    // Reauthorize with 'both' purpose to get all scopes
    handleConnect(provider, 'both');
  };

  const handleDisconnect = (
    connectionId: string,
    providerName: string,
    username: string,
    purpose: string,
  ) => {
    // Show confirmation modal
    setDisconnectModal({
      isOpen: true,
      connectionId,
      providerName,
      username,
      purpose,
    });
  };

  const confirmDisconnect = async () => {
    if (!disconnectModal.connectionId) return;

    try {
      await api.delete(`/oauth/connections/${disconnectModal.connectionId}`);

      // Reload data
      await loadData();
    } catch (err: unknown) {
      logger.error('Failed to disconnect connection', { error: err });
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || err.message || 'Failed to disconnect connection');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to disconnect connection');
      }
    }
  };

  const handleAvatarError = (avatarUrl: string) => {
    setFailedAvatars((prev) => new Set(prev).add(avatarUrl));
  };

  const handleInstallGitHubApp = (installationUrl: string) => {
    window.location.href = installationUrl;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading OAuth providers...</p>
        </div>
      </div>
    );
  }

  const authProviders = providers.filter((p) => isAuthProvider(p.name));
  const repoProviders = providers.filter((p) => isRepositoryProvider(p.name));

  return (
    <div className="min-h-screen flex flex-col bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 sm:px-6 lg:px-8">
      <Navbar pageTitle="Account Settings" showDivider={true} />

      <main className="flex-1 max-w-7xl mx-auto py-8 pt-24 w-full">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            Account Settings
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage your profile, authentication providers, and repository access.
          </p>
        </div>

        <TabNav activeTab={activeTab} onTabChange={handleTabChange} />

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
            <div className="flex">
              <div className="shrink-0">
                <AlertCircle className="w-4 h-4 text-red-400" />
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-red-800 dark:text-red-400">Error</h3>
                <div className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Reauth Banner */}
        {reauthConnections.length > 0 && (
          <ReauthBanner
            connections={reauthConnections}
            onReauthorize={handleReauthorize}
            onDismiss={() => setReauthConnections([])}
          />
        )}

        {activeTab === 'profile' && (
          <ProfileTab
            profileData={profileData}
            setProfileData={setProfileData}
            profileLoading={profileLoading}
            profileSuccess={profileSuccess}
            onProfileSubmit={handleProfileUpdate}
            passwordData={passwordData}
            setPasswordData={setPasswordData}
            passwordLoading={passwordLoading}
            passwordSuccess={passwordSuccess}
            passwordError={passwordError}
            hasPassword={hasPassword}
            onPasswordSubmit={handlePasswordChange}
          />
        )}

        {activeTab === 'auth' && (
          <AuthProvidersTab
            authProviders={authProviders}
            failedAvatars={failedAvatars}
            onAvatarError={handleAvatarError}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onInstallGitHubApp={handleInstallGitHubApp}
          />
        )}

        {activeTab === 'repository' && (
          <RepoProvidersTab
            repoProviders={repoProviders}
            failedAvatars={failedAvatars}
            onAvatarError={handleAvatarError}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onInstallGitHubApp={handleInstallGitHubApp}
          />
        )}

        {activeTab === 'api-tokens' && <ApiTokensTab />}
      </main>

      {/* Disconnect Confirmation Modal */}
      <ConfirmModal
        isOpen={disconnectModal.isOpen}
        onClose={() => setDisconnectModal({ ...disconnectModal, isOpen: false })}
        onConfirm={confirmDisconnect}
        title="Disconnect Account?"
        message={`Are you sure you want to disconnect ${disconnectModal.providerName} account "${disconnectModal.username}" (${disconnectModal.purpose === 'auth' ? 'authentication' : disconnectModal.purpose === 'repository' ? 'repository access' : 'authentication and repository access'})?`}
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="danger"
      />

      <Footer />
    </div>
  );
};

export default OAuthSettingsPage;
